import React, { useState, useEffect } from 'react';
import SalesDetailPage from './SalesDetailPage';
import { currencyFormatter } from '../utils/reportingUtils';
import { buildChargebacksFilterGroups } from '../constants/chargebacksFiltersConfig';
import { SORT_TYPES } from '../constants/chargebacksColumnsConfig';

const axios = require('axios').default;

const CHARGEBACKS_COLUMNS = [
  { id: 'disputed_at', label: 'Disputed Date', alwaysVisible: false, defaultVisible: true, sortable: true, sortType: 'date' },
  { id: 'event_name', label: 'Event Name', alwaysVisible: false, defaultVisible: true, sortable: true, sortType: 'text' },
  { id: 'event_date', label: 'Event Date', alwaysVisible: false, defaultVisible: true, sortable: true, sortType: 'date' },
  { id: 'venue_name', label: 'Venue Name', alwaysVisible: false, defaultVisible: true, sortable: true, sortType: 'text' },
  { id: 'reason', label: 'Reason', alwaysVisible: false, defaultVisible: true, sortable: true, sortType: 'text' },
  { id: 'state', label: 'State', alwaysVisible: false, defaultVisible: true, sortable: true, sortType: 'text' },
  { id: 'amount', label: 'Amount', alwaysVisible: false, defaultVisible: true, sortable: true, sortType: 'number' },
  { id: 'order_name', label: 'Customer', alwaysVisible: false, defaultVisible: true, sortable: true, sortType: 'text' },
  { id: 'order_email', label: 'Email', alwaysVisible: false, defaultVisible: false, sortable: false, sortType: 'text' },
];

const getDefaultVisibleColumns = () =>
  CHARGEBACKS_COLUMNS.filter(c => c.defaultVisible);

const getDefaultHiddenColumns = () =>
  CHARGEBACKS_COLUMNS.filter(c => !c.defaultVisible && !c.alwaysVisible);

const formatEventDate = (dateStr) => {
  if (!dateStr) return '\u2014';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatEventDateTime = (dateStr) => {
  if (!dateStr) return '\u2014';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const stateColors = {
  open: { bg: 'var(--sl-bg-warning-primary, #fff4e5)', color: 'var(--sl-text-warning, #b54708)' },
  submitted: { bg: 'var(--sl-bg-brand-primary, #eff8ff)', color: 'var(--sl-fg-brand, #175cd3)' },
  won: { bg: 'var(--sl-bg-success-primary, #ecfdf3)', color: 'var(--sl-text-success, #027a48)' },
  lost: { bg: 'var(--sl-bg-error-primary, #fef3f2)', color: 'var(--sl-text-error, #b42318)' },
  closed: { bg: 'var(--sl-bg-tertiary, #f2f4f7)', color: 'var(--sl-text-secondary, #344054)' },
};

const CHARGEBACKS_CONFIG = {
  pageTitle: 'Chargebacks',
  pageSubtitle: 'Payment disputes across your venues.',
  sidebarKey: 'finances_chargebacks',
  defaultPreset: 'year_to_date',
  dateModeOptions: [
    { key: 'event_date', label: 'Event Date' },
  ],

  columns: CHARGEBACKS_COLUMNS,
  sortTypes: SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
  localStorageKey: (teamId) => `reportingFinancesChargebacks_${teamId}`,
  firstColumnId: 'disputed_at',
  defaultSortRules: [{ field: 'disputed_at', direction: 'desc' }],

  kpiFromDetailResponse: false,
  salesDataKey: null,
  venueSeriesKey: null,
  exportBasePath: (teamId) => `/teams/${teamId}/reporting/finances/chargebacks/export`,
  detailEndpoint: (teamId) => `/teams/${teamId}/reporting/finances/chargebacks/data.json`,
  detailResponseKey: 'chargebacks',
  detailErrorMessage: 'Failed to load chargebacks data.',
  detailTableTitle: 'Chargebacks',
  emptyMessage: 'No chargebacks found.',
  searchPlaceholder: 'Search chargebacks...',
  paginationLabel: 'chargebacks',
  itemLabel: (count) => `chargeback${count !== 1 ? 's' : ''}`,

  buildKpiCards: () => [],
  kpiGridColumns: 0,

  overTimeKey: null,
  salesCardTitle: null,
  chartColor: '#FF595E',

  topCharts: [],

  groupByOptions: null,
  groupByDefault: null,

  renderCellValue: (col, row) => {
    switch (col.id) {
      case 'disputed_at':
        return <span className="text-nowrap">{formatEventDateTime(row.disputed_at)}</span>;
      case 'event_name':
        return row.event_name || '\u2014';
      case 'event_date':
        return <span className="text-nowrap">{row.event_date ? formatEventDate(row.event_date) : '\u2014'}</span>;
      case 'venue_name':
        return row.venue_name || '\u2014';
      case 'reason':
        return row.reason ? row.reason.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '\u2014';
      case 'state': {
        const colors = stateColors[row.state] || stateColors.closed;
        return row.state
          ? <span className="badge" style={{ background: colors.bg, color: colors.color, fontSize: 12 }}>{row.state}</span>
          : '\u2014';
      }
      case 'amount':
        return currencyFormatter.format(row.amount || 0);
      case 'order_name':
        return row.order_name || '\u2014';
      case 'order_email':
        return row.order_email || '\u2014';
      default:
        return '\u2014';
    }
  },

  filterTitle: 'Filter Chargebacks',
  filterSubtitle: 'Apply filters to narrow chargeback data.',
};

const buildDetailApiFilters = (activeFilters) => {
  const params = {};
  if (activeFilters.venue_name && activeFilters.venue_name.length > 0) {
    params.venue_name = activeFilters.venue_name;
  }
  if (activeFilters.event_name && activeFilters.event_name.length > 0) {
    params.event_name = activeFilters.event_name;
  }
  if (activeFilters.state && activeFilters.state.length > 0) {
    params.state = activeFilters.state;
  }
  if (activeFilters.reason && activeFilters.reason.length > 0) {
    params.reason = activeFilters.reason;
  }
  if (activeFilters.calendar_classification && activeFilters.calendar_classification.length > 0) {
    params.calendar_classification = activeFilters.calendar_classification;
  }
  return params;
};

function ReportingFinancesChargebacksPage({ team, csrfToken, ...props }) {
  const [filterOptions, setFilterOptions] = useState({});

  useEffect(() => {
    if (!team || !team.id) return;
    if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    axios.get(`/teams/${team.id}/reporting/finances/chargebacks/filter_options.json`)
      .then((res) => setFilterOptions(res.data || {}))
      .catch(() => {});
  }, [team && team.id, csrfToken]);

  const configWithFilters = {
    ...CHARGEBACKS_CONFIG,
    buildFilterGroups: () => buildChargebacksFilterGroups(filterOptions),
    buildDetailApiFilters,
  };
  return <SalesDetailPage {...props} team={team} csrfToken={csrfToken} config={configWithFilters} />;
}

export default ReportingFinancesChargebacksPage;
